import React, { useState, useEffect } from 'react';
import { Maximize2, MinusCircle, PlusCircle, Save, Play, Pause, RotateCcw, DollarSign, Users, Clock } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Space {
  id: string;
  name: string;
  type: 'meeting' | 'desk' | 'collaboration' | 'social' | 'storage';
  area: number;
  capacity: number;
  utilization: number;
  peakHours: string;
  costPerSqFt: number;
  adjacentSpaces: string[];
}

interface OptimizationPlan {
  id: string;
  name: string;
  description: string;
  spaces: string[];
  proposedChanges: {
    spaceId: string;
    newType: Space['type'];
    newCapacity: number;
    estimatedCost: number;
  }[];
  roi: number;
  implementationTime: string;
}

const SpaceUtilizationOptimizer: React.FC = () => {
  // State for spaces
  const [spaces, setSpaces] = useState<Space[]>([
    {
      id: 's1',
      name: 'Conference Room A',
      type: 'meeting',
      area: 400,
      capacity: 12,
      utilization: 35,
      peakHours: '10:00 AM - 2:00 PM',
      costPerSqFt: 45,
      adjacentSpaces: ['s2', 's3']
    },
    {
      id: 's2',
      name: 'Open Workspace North',
      type: 'desk',
      area: 800,
      capacity: 24,
      utilization: 65,
      peakHours: '9:00 AM - 3:00 PM',
      costPerSqFt: 38,
      adjacentSpaces: ['s1', 's4']
    },
    {
      id: 's3',
      name: 'Breakout Area',
      type: 'collaboration',
      area: 300,
      capacity: 8,
      utilization: 28,
      peakHours: '11:00 AM - 4:00 PM',
      costPerSqFt: 42,
      adjacentSpaces: ['s1', 's4']
    }
  ]);

  // State for optimization plans
  const [plans, setPlans] = useState<OptimizationPlan[]>([]);
  
  // State for selected space and editing
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  
  // State for simulation
  const [isSimulating, setIsSimulating] = useState(false);
  const [utilizationData, setUtilizationData] = useState<any[]>([]);

  // Effect for simulation data
  useEffect(() => {
    if (isSimulating) {
      const interval = setInterval(() => {
        const now = new Date();
        const newDataPoint = {
          time: now.toLocaleTimeString(),
          currentUtilization: Math.floor(Math.random() * 40) + 30,
          projectedUtilization: Math.floor(Math.random() * 30) + 60,
          costPerPerson: Math.floor(Math.random() * 20) + 40
        };
        
        setUtilizationData(prev => [...prev.slice(-20), newDataPoint]);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isSimulating]);

  // Calculate optimization opportunities
  const getOptimizationOpportunities = () => {
    return spaces
      .filter(space => space.utilization < 40)
      .sort((a, b) => a.utilization - b.utilization);
  };

  // Calculate potential savings
  const calculatePotentialSavings = (space: Space) => {
    const underutilizedPercentage = (40 - space.utilization) / 100;
    return space.area * space.costPerSqFt * underutilizedPercentage;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-4">
        <h2 className="text-2xl font-bold">Space Utilization Optimizer</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setIsSimulating(!isSimulating)}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {isSimulating ? (
              <>
                <Pause className="w-5 h-5" />
                <span>Pause Simulation</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Start Simulation</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowPlanForm(true)}
            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            <Save className="w-5 h-5" />
            <span>Create Optimization Plan</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Space List and Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Spaces Overview</h3>
          <div className="space-y-4">
            {spaces.map(space => (
              <div
                key={space.id}
                className={`border rounded-lg p-4 cursor-pointer ${
                  selectedSpace === space.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedSpace(space.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{space.name}</h4>
                    <div className="text-sm text-gray-500">
                      {space.type.charAt(0).toUpperCase() + space.type.slice(1)} Space
                    </div>
                    <div className="text-sm">
                      Area: {space.area} sq ft | Capacity: {space.capacity}
                    </div>
                    <div className="text-sm">
                      Peak Hours: {space.peakHours}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className={`px-2 py-1 rounded text-sm ${
                      space.utilization < 40 ? 'bg-red-100 text-red-800' :
                      space.utilization < 70 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {space.utilization}% utilized
                    </span>
                    <div className="text-sm text-gray-500 mt-1">
                      ${space.costPerSqFt}/sq ft
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Optimization Opportunities */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Optimization Opportunities</h3>
          <div className="space-y-4">
            {getOptimizationOpportunities().map(space => (
              <div key={space.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{space.name}</h4>
                    <div className="text-sm text-gray-500">
                      Current: {space.type.charAt(0).toUpperCase() + space.type.slice(1)} Space
                    </div>
                    <div className="text-sm">
                      Utilization: {space.utilization}%
                    </div>
                    <div className="text-sm text-red-600">
                      Potential Annual Savings: ${calculatePotentialSavings(space).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="text-sm font-medium">Recommended Actions:</div>
                    <div className="text-sm">
                      {space.utilization < 30 ? 'Consider repurposing' :
                       space.utilization < 40 ? 'Optimize scheduling' :
                       'Monitor usage patterns'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Utilization Trends */}
      {isSimulating && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Real-time Utilization Trends</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={utilizationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="currentUtilization" 
                  stroke="#3b82f6" 
                  name="Current Utilization %"
                />
                <Line 
                  type="monotone" 
                  dataKey="projectedUtilization" 
                  stroke="#10b981" 
                  name="Projected Utilization %"
                />
                <Line 
                  type="monotone" 
                  dataKey="costPerPerson" 
                  stroke="#ef4444" 
                  name="Cost per Person ($)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Optimization Plan Form */}
      {showPlanForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[800px] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Create Optimization Plan</h3>
              <button
                onClick={() => setShowPlanForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <MinusCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Plan Details Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="e.g., Q4 Space Optimization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border rounded-md"
                  rows={3}
                  placeholder="Describe the optimization goals and approach"
                />
              </div>

              {/* Space Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spaces to Optimize
                </label>
                <div className="space-y-2">
                  {spaces.map(space => (
                    <label key={space.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                      />
                      <span>{space.name} ({space.utilization}% utilized)</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Proposed Changes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proposed Changes
                </label>
                <div className="space-y-2">
                  {spaces.filter(s => s.utilization < 40).map(space => (
                    <div key={space.id} className="border rounded-lg p-4">
                      <h4 className="font-medium">{space.name}</h4>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <div>
                          <label className="block text-sm text-gray-600">New Type</label>
                          <select className="w-full px-3 py-2 border rounded-md mt-1">
                            <option value="meeting">Meeting Room</option>
                            <option value="desk">Desk Space</option>
                            <option value="collaboration">Collaboration Area</option>
                            <option value="social">Social Space</option>
                            <option value="storage">Storage</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600">New Capacity</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-md mt-1"
                            placeholder="Enter new capacity"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Implementation Timeline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Implementation Timeline
                </label>
                <select className="w-full px-3 py-2 border rounded-md">
                  <option value="1month">1 Month</option>
                  <option value="3months">3 Months</option>
                  <option value="6months">6 Months</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowPlanForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Create optimization plan
                  setShowPlanForm(false);
                }}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Create Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceUtilizationOptimizer; 